import React, { useState, useRef, useEffect } from 'react';
import { useCms } from '../context/CmsContext';

const fallbackCases = [
  {
    id: 'soroe-kvist',
    title: 'Komplet laserafrensning af Sorø station fra 1856',
    category: 'Facader',
    before: 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/soroe-kvist-foer.jpg',
    after: 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/soroekvist-efter.jpg',
    description: 'Bevaringsværdig kulturarv kræver den mest skånsomme behandling.',
    weight: 1
  },
  {
    id: 'murvaerk',
    title: 'Nybyggeri Nordsjælland',
    category: 'Facader',
    before: 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/mur-for-1800x1800-1.jpg',
    after: 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/mur-efter-1800x1800-1.jpg',
    description: 'Nænsom laserrens af nybyggeri nordsjælland. Fjerner effektivt snavs, sod og belægninger helt uden vand eller kemikalier.',
    weight: 2
  },
  {
    id: 'figur',
    title: 'Fredede sandstensfigurer',
    category: 'Specialopgaver',
    before: 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/figur-fa-r-og-efter-1800-x-1800.jpg',
    after: 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/figur-efter-1800x1800.jpg',
    description: 'Skånsom laserrens af fredede sandstensfigurer, Københavns Hovedbanegård.',
    weight: 3
  },
  {
    id: 'metal',
    title: 'Rust på maskindel',
    category: 'Metal',
    before: 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/metal-rust-800-x-800-px-4.jpg',
    after: 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/metal-til-hjemmeside-slider-efter-800-x-800-px-1-1.jpg',
    description: 'Præcisionslaserrens på maskindel i Silkeborg. Fjerner effektivt rust og oxidlag uden at skade de originale produktionsriller.',
    weight: 4
  }
];

export function SingleSlider({ caseData }) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const position = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(position);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      handleMove(e.clientX);
    };

    const handleTouchMove = (e) => {
      if (!isDragging) return;
      if (e.touches.length > 0) {
        handleMove(e.touches[0].clientX);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('touchmove', handleTouchMove, { passive: true });
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  // Apply Cloudflare image resizing on-the-fly for optimal performance
  const beforeUrl = caseData.before.includes('assets.auxodev.dk') && !caseData.before.includes('/cdn-cgi/image/')
    ? caseData.before.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/')
    : caseData.before;

  const afterUrl = caseData.after.includes('assets.auxodev.dk') && !caseData.after.includes('/cdn-cgi/image/')
    ? caseData.after.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/')
    : caseData.after;

  return (
    <div className="w-full flex flex-col gap-4 bg-white p-4 rounded-xl border border-zinc-100 shadow-sm hover:shadow-md transition-shadow duration-300">
      {/* Interactive Slider Container - strictly square (aspect-square) */}
      <div 
        ref={containerRef}
        className="relative w-full aspect-square rounded-lg overflow-hidden bg-zinc-50 shadow-inner select-none cursor-ew-resize"
        onMouseDown={(e) => {
          e.preventDefault();
          setIsDragging(true);
          handleMove(e.clientX);
        }}
        onTouchStart={(e) => {
          setIsDragging(true);
          if (e.touches.length > 0) {
            handleMove(e.touches[0].clientX);
          }
        }}
      >
        {/* After Image (Bottom) */}
        <img
          src={afterUrl}
          alt="Efter laserrensning"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />

        {/* Before Image (Top, Clipped) */}
        <div
          className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none"
          style={{ clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)` }}
        >
          <img
            src={beforeUrl}
            alt="Før laserrensning"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          />
        </div>

        {/* Slider Line */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-brand-green pointer-events-none"
          style={{ left: `${sliderPosition}%` }}
        >
          {/* Handle */}
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-brand-green text-zinc-950 border-2 border-white flex items-center justify-center shadow-lg">
            <i className="fa-solid fa-arrows-left-right text-xs" />
          </div>
        </div>

        {/* Badges */}
        <div className="absolute bottom-3 left-3 bg-zinc-950/80 backdrop-blur-xs text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm pointer-events-none">
          Før
        </div>
        <div className="absolute bottom-3 right-3 bg-brand-green/90 text-zinc-950 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm pointer-events-none">
          Efter
        </div>
      </div>

      {/* Title & Description */}
      <div className="text-left flex flex-col gap-1 px-1">
        <span className="text-xs font-bold uppercase tracking-wider text-brand-green">
          {caseData.category}
        </span>
        <h3 className="text-lg font-bold text-zinc-900 leading-snug">
          {caseData.title}
        </h3>
        {caseData.description && (
          <p className="text-zinc-600 text-sm leading-relaxed mt-1">
            {caseData.description}
          </p>
        )}
      </div>
    </div>
  );
}

export default function BeforeAfterSlider({ limit, columns = 4 }) {
  const { gallery } = useCms();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (gallery && gallery.length > 0) {
      const filtered = gallery
        .filter((item) => item.before_image?.length > 0 && item.after_image?.length > 0)
        .map((item) => ({
          id: item.id,
          title: item.title?.da || item.title || '',
          category: item.category || 'Specialopgave',
          before: item.before_image[0],
          after: item.after_image[0],
          description: item.description?.da || item.description || '',
          weight: item.weight !== undefined && item.weight !== null ? Number(item.weight) : 9999
        }))
        .sort((a, b) => a.weight - b.weight);

      setCases(filtered.length > 0 ? filtered : fallbackCases);
    } else {
      setCases(fallbackCases);
    }
  }, [gallery]);

  const gridClasses = columns === 5
    ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-7xl mx-auto"
    : columns === 2
    ? "grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto"
    : columns === 3
    ? "grid grid-cols-1 md:grid-cols-3 gap-6 max-w-7xl mx-auto"
    : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto";

  if (loading) {
    return (
      <div className={gridClasses}>
        {[1, 2, 3, 4].slice(0, columns === 2 ? 2 : 4).map((n) => (
          <div key={n} className="w-full flex flex-col gap-4 bg-white p-4 rounded-xl border border-zinc-100 shadow-sm animate-pulse">
            <div className="w-full aspect-square bg-zinc-100 rounded-lg" />
            <div className="h-4 bg-zinc-100 rounded w-1/4" />
            <div className="h-6 bg-zinc-100 rounded w-3/4" />
            <div className="h-4 bg-zinc-100 rounded w-full" />
          </div>
        ))}
      </div>
    );
  }

  const displayedCases = limit ? cases.slice(0, limit) : cases;

  return (
    <div className={gridClasses}>
      {displayedCases.map((c) => (
        <SingleSlider key={c.id} caseData={c} />
      ))}
    </div>
  );
}
